import React, { useEffect, useState } from 'react';
import {
  TextField,
  MenuItem,
  Typography,
  Box,
  useTheme,
  InputAdornment,
  Alert,
  AlertTitle,
  Tooltip,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { useApolloClient } from '@apollo/client';
import styles from './style';
import {
  fetchESIMManufacturers,
  handleBrandChange,
  handleEidChange,
  handleESIMDetailsUpdate,
} from './simtypeSelector.util';
import closeFilled from '../../../public/icons/CloseFilled.png';

const CheckoutESIMDetails = ({ offerId, cmsData }) => {
  const theme = useTheme();
  const graphql = useApolloClient();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [brand, setBrand] = useState('');
  const [eid, setEid] = useState('');
  const [isValidEid, setIsValidEid] = useState(false);
  const [eidError, setEidError] = useState('');
  const [brandOptions, setBrandOptions] = useState([]);
  useEffect(() => {
    const loadESIMManufacturers = async () => {
      const apiBrands = await fetchESIMManufacturers(graphql);

      const mappedBrands = apiBrands.map(brandName => ({
        label: brandName.toLowerCase().replace(/(^\w|\s\w)/g, match => match.toUpperCase()),
        value: brandName,
      }));

      const otherBrands = [{ label: 'Other', value: 'other' }];
      setBrandOptions([...mappedBrands, ...otherBrands]);
    };
    loadESIMManufacturers();
  }, []);
  useEffect(() => {
    if (eid.length === 32 && isValidEid) {
      handleESIMDetailsUpdate({ eid, manufacturer: brand, graphql, offerId, url: '/shop/summary' });
    }
  }, [eid, isValidEid]);

  const getEndAdornment = () => {
    if (eid.length === 32 && isValidEid) {
      return (
        <InputAdornment position='end'>
          <CheckIcon sx={{ color: 'green' }} />
        </InputAdornment>
      );
    }

    if (eid.length === 32 && !isValidEid) {
      return (
        <InputAdornment position='end'>
          <Box component='img' src={closeFilled.src} alt='' />
        </InputAdornment>
      );
    }

    return null;
  };

  return (
    <Box sx={styles(theme).brandContainer}>
      <Typography variant='body1' sx={styles(theme).description}>
        {cmsData?.Summary_ESIMDetails?.brandRequestMessage}
      </Typography>

      <Box sx={styles(theme).checkoutFieldsContainer}>
        <TextField
          select
          label='Select brand'
          variant='outlined'
          size='small'
          value={brand}
          onChange={e => handleBrandChange(e, setBrand, setEid, setIsValidEid, setEidError)}
          fullWidth
          sx={styles(theme).select}
          SelectProps={{
            IconComponent: ExpandMoreIcon,
          }}
        >
          {brandOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {brand &&
          (brand === 'other' ? (
            <Alert
              severity='info'
              sx={{
                '& .MuiSvgIcon-root': { color: theme.palette.secondary.OfferSelected },
                width: 'fit-content',
                flexGrow: 1,
                padding: '0px 16px',
              }}
            >
              <AlertTitle
                sx={{
                  marginBottom: '0px',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{
                    color: theme.palette.secondary.OfferSelected,
                  }}
                >
                  {cmsData?.Summary_ESIMDetails?.otherBrandNotification}
                </Typography>
              </AlertTitle>
            </Alert>
          ) : (
            <Box sx={styles(theme).eidBox}>
              <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                <TextField
                  label='EID number'
                  value={eid}
                  variant='outlined'
                  size='small'
                  onChange={e =>
                    handleEidChange(
                      e,
                      brand,
                      setEid,
                      isValidEid,
                      setIsValidEid,
                      setEidError,
                      graphql,
                      offerId
                    )
                  }
                  placeholder='Enter your EID'
                  fullWidth
                  error={!isValidEid}
                  helperText={
                    <Typography variant='body2' sx={{ color: theme.palette.error.main }}>
                      {eidError}
                    </Typography>
                  }
                  inputProps={{
                    maxLength: 32,
                  }} /* eslint-disable-next-line react/jsx-no-duplicate-props */
                  InputProps={{
                    endAdornment: getEndAdornment(),
                  }}
                  sx={styles(theme).eidInput}
                />
                {brand && (
                  <InputAdornment sx={{ marginTop: '1.2rem' }}>
                    <Tooltip
                      title={
                        <div>
                          <div style={{ marginBottom: '1rem' }}>
                            <span style={{ fontWeight: 'bold' }}>Apple:</span>{' '}
                            <span style={{ fontWeight: '400' }}>
                              The EID is typically found under Settings &gt; General &gt; About.
                            </span>
                          </div>
                          <div style={{ marginBottom: '1rem' }}>
                            <span style={{ fontWeight: 'bold' }}>Samsung:</span>{' '}
                            <span style={{ fontWeight: '400' }}>
                              To find the EID on a Samsung phone, navigate to Settings &gt; About
                              phone &gt; Status information.
                            </span>
                          </div>
                          <div>
                            <span style={{ fontWeight: 'bold' }}>Other brands:</span>{' '}
                            <span style={{ fontWeight: '400' }}>
                              If your phone is not listed in the dropdown, you can select continue
                              and we will send you a QR code to activate your eSIM.
                            </span>
                          </div>
                        </div>
                      }
                      placement={isMobile ? 'left' : 'right'}
                      PopperProps={{
                        modifiers: [
                          {
                            name: 'flip',
                            enabled: true,
                            options: {
                              fallbackPlacements: ['left', 'bottom'],
                            },
                          },
                        ],
                      }}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            maxWidth: 300,
                            whiteSpace: 'normal',
                            padding: '0.75rem',
                            fontSize: '0.875rem',
                            zIndex: 1500,
                          },
                        },
                      }}
                    >
                      <IconButton size='small' sx={styles(theme).eidToolIcon}>
                        <InfoOutlined fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )}
              </Box>
              <Typography variant='textFeildMessage' sx={styles(theme).notice}>
                {cmsData?.Summary_ESIMDetails?.eidTextFeildMessage}
              </Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default CheckoutESIMDetails;
